import { Page } from "@shopify/polaris";
import { IShopifyGetThemesResponse, getShopifyThemes } from "@smartrr/shared/shopifyRest/theme";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { useRestClient } from "@vendor-app/app/AdminRoute/components/auth/RestProviderWrapper";
import { ThemingStoreAccess } from "./store";

import { Theming } from "./sections/Main";
import { Spinner } from "../components/elements/Spinner";
import { useThemeSidebarSections } from "./sections/Main/hooks";
import { defaultCustomerPortalThemeConfig } from "@smartrr/shared/themes/defaultThemeConfig";
import { ICustomerPortalTheme, ICustomerPortalThemeForm } from "@smartrr/shared/entities/CustomerPortalTheme";

const PageWrapper = styled.div`
  & > .Polaris-Page--fullWidth {
    padding: 0;
  }
  & > * .Polaris-Page__Content {
    margin-top: 0;
  }
`;

export function AdminThemingRoute(): JSX.Element {
  const restClient = useRestClient();
  const isThemeLoading = ThemingStoreAccess.useIsLoading();
  const currentThemeValues = ThemingStoreAccess.useCurrentThemeValues() as ICustomerPortalThemeForm;
  const { get: getCustomerPortalTheme, updateDefaultThemeValues } = ThemingStoreAccess.useActions();
  const { showError } = ThemingStoreAccess.useErrors();
  const { addToast } = useToast();

  const [shopifyThemes, setShopifyThemes] = useState<IShopifyGetThemesResponse["themes"]>([]);
  const [mainThemeLoading, setMainThemeLoading] = useState(true);

  const getMainThemeId = useCallback(async () => {
    const res = await getShopifyThemes(restClient);

    if (res.type === "success") {
      setShopifyThemes(res.body.themes || []);
    }

    if (res.type === "error" || !res.body.themes?.find(t => t.role === "main")) {
      addToast("Error getting main theme id");
      setMainThemeLoading(false);
      return;
    }

    setMainThemeLoading(false);
  }, []);

  useEffect(() => {
    getCustomerPortalTheme();
    getMainThemeId();
  }, []);

  useEffect(() => {
    if (showError) {
      addToast(`Error getting theme`, true);
    }
  }, [showError]);

  const { updatedSectionOrder, updatedSectionValues } = useThemeSidebarSections(
    currentThemeValues ?? defaultCustomerPortalThemeConfig
  );

  useEffect(() => {
    if (currentThemeValues) {
      updateDefaultThemeValues("modernThemeSettings", {
        ...currentThemeValues.modernThemeSettings,
        sections: updatedSectionValues,
        sectionsOrder: updatedSectionOrder,
      });
    }
  }, []);

  const areThemesLoading = isThemeLoading || mainThemeLoading;

  const hasLoaded = (
    themeValues: ICustomerPortalTheme | null,
    isLoading: boolean
  ): themeValues is NonNullable<ICustomerPortalTheme> => !!themeValues && !isLoading;

  if (!hasLoaded(currentThemeValues, areThemesLoading)) {
    return <Spinner />;
  }

  return (
    <React.Fragment>
      <PageWrapper>
        <Page fullWidth>
          <Theming themes={shopifyThemes} currentThemeValues={currentThemeValues!} />
        </Page>
      </PageWrapper>
    </React.Fragment>
  );
}
